import axios, { AxiosInstance, AxiosResponse } from "axios";
import { DispatchWithoutAction, useCallback, useEffect, useState } from "react";
import { ILoaded } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { CodeExchange, IvrResponse } from "../../../Models/Authentication";
import { User } from "../../../Models/User";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import EnvironmentConfiguration from "../../Constants/EnvironmentConfiguration";
import { IvrOAuthConfiguration } from "../Types/OAuthConfiguration";
import useGetOAuthCode, { oAuthErrorMessageMap } from "./UseGetOAuthCode";

class ConfiguredIvrOAuthConfiguration extends IvrOAuthConfiguration {
  constructor(url: string) {
    super(url, EnvironmentConfiguration.oauth.inContact.clientId, EnvironmentConfiguration.oauth.redirectPath);
  }
}

interface IUsePutUserIvrIdProps {
  /** The initial state of the user to be updated with identity information. */
  user: User;
}
interface IUsePutUserIvrIdResponse extends ILoaded {
  errorMessage: Nullable<string>;
  /** The ivr identity for the requested user after the request process is complete. */
  userIvrId: Nullable<string>;
  /** Callback that the update process should begin. */
  execute: DispatchWithoutAction;
}

/** Updates the user's external ivr identity after Authenticating the user with that system.
 * @remarks process summary: Get Ivr OAuth Url => Open Window For Auth => Dispatch Ivr Auth Credentials
 * to Gem API => Persist User with Ivr Identity.
 */
const usePutUserIvrId = ({ user }: IUsePutUserIvrIdProps): IUsePutUserIvrIdResponse => {
  const [ivrCodeExchange, setIvrCodeExchange] = useState<CodeExchange>();
  const [authEndpointPath, setAuthEndpointPath] = useState<string>();
  const { isLoading, errorCode, code, sendRequest } = useGetOAuthCode();

  useEffect(() => {
    if (authEndpointPath) sendRequest(new ConfiguredIvrOAuthConfiguration(authEndpointPath));
  }, [authEndpointPath]);

  useEffect(() => {
    if (code) setIvrCodeExchange({ code, redirectUri: EnvironmentConfiguration.oauth.redirectPath });
  }, [code]);

  const {
    response: postTokenResponse,
    error: postTokenError,
    isLoading: postTokenLoading
  } = useApi<IvrResponse, CodeExchange>({
    method: "post",
    url: "Legacy/V0/ivr/token",
    data: ivrCodeExchange,
    error: Error.PostIvrToken,
    valid: Boolean(ivrCodeExchange)
  });

  const {
    response: putUserMeResponse,
    isLoading: putUserMeLoading,
    error: putUserMeError
  } = useApi<User, User>({
    method: "put",
    url: "Legacy/V0/User/Me",
    data: { ...user, externalIds: { ...user.externalIds, ivr: `${postTokenResponse?.agentId}` } },
    config: { headers: { "x-gem-ivr-session-key": postTokenResponse?.key || "" } },
    valid: !!postTokenResponse?.agentId && !!postTokenResponse?.key,
    error: Error.PutUserMe
  });

  const execute = useCallback(() => {
    const cxOneApi: AxiosInstance = axios.create({ baseURL: process.env.REACT_APP_OAUTH_INCONTACT_URL });
    cxOneApi
      .get("/.well-known/openid-configuration")
      .then(({ data }: AxiosResponse<{ authorization_endpoint: string }>) =>
        setAuthEndpointPath(data.authorization_endpoint)
      );
  }, []);

  return {
    errorMessage:
      errorCode !== null
        ? oAuthErrorMessageMap[errorCode]
        : putUserMeError || postTokenError
        ? "An Error Occurred"
        : null,
    isLoading: isLoading || postTokenLoading || putUserMeLoading,
    execute,
    userIvrId: putUserMeResponse?.externalIds.ivr || null
  };
};

export default usePutUserIvrId;
